// In-memory sales orders for demo mode (no Supabase configured). Mirrors the
// RPC shapes so the screens behave the same; reservation is simplified to a
// fixed stock per item, shared by the released demo orders.
import type { Page } from "@/components/DataList";
import type {
  ListParams,
  SalesOrderDocument,
  SalesOrderHeaderInput,
  SalesOrderLine,
  SalesOrderLineInput,
  SalesOrderRow,
  SalesOrderStatus,
  SaveResult,
  Transition,
} from "./api";

const customers = [
  { id: "bp-autoseat", code: "AUTOSEAT", name: "AutoSeat Assembly s.r.o.", currency: "EUR", terms: "30 days net" },
  { id: "bp-busline", code: "BUSLINE", name: "BusLine Interiors Sp. z o.o.", currency: "PLN", terms: "14 days net" },
  { id: "bp-railco", code: "RAILCO", name: "RailCo Seating GmbH", currency: "EUR", terms: "45 days net" },
];

const catalog = [
  { id: "it-st600", code: "ST-600", description: "Seat assembly, grey fabric", price: 389, stock: 60 },
  { id: "it-st610", code: "ST-610", description: "Seat assembly, black fabric", price: 412, stock: 25 },
];

const isoDay = (offset: number) => {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
};

function line(no: number, item: (typeof catalog)[number], qty: number, price: number | null, requested: string): SalesOrderLine {
  return {
    id: `sl-${no}-${Math.random().toString(36).slice(2, 8)}`,
    line_no: no,
    item_id: item.id,
    item_code: item.code,
    item_description: item.description,
    lot_controlled: false,
    ordered_qty: String(qty),
    uom_id: "u-pcs",
    uom_code: "pcs",
    uom_decimals: 0,
    ordered_base_qty: String(qty),
    reserved_base_qty: "0",
    shipped_base_qty: "0",
    base_uom_code: "pcs",
    base_uom_decimals: 0,
    unit_price: price === null ? null : price.toFixed(4),
    line_amount: price === null ? null : (qty * price).toFixed(4),
    requested_date: requested,
    warehouse_id: "wh-main",
    warehouse_code: "MAIN",
    available_base_qty: "0",
    status: "open",
  };
}

function build(
  id: string,
  number: string,
  status: SalesOrderStatus,
  cust: (typeof customers)[number],
  orderDate: string,
  lines: SalesOrderLine[],
): SalesOrderDocument {
  const open = lines.filter((l) => l.status === "open" && l.requested_date).map((l) => l.requested_date!);
  return {
    id,
    number,
    status,
    company_id: "demo-pl01",
    company_code: "PL01",
    customer_id: cust.id,
    customer_code: cust.code,
    customer_name: cust.name,
    order_date: orderDate,
    currency: cust.currency,
    customer_reference: null,
    payment_terms: cust.terms,
    ship_to: null,
    note: null,
    warehouse_id: "wh-main",
    warehouse_code: "MAIN",
    next_requested_date: open.sort()[0] ?? null,
    total_amount: lines.reduce((s, l) => s + Number(l.line_amount ?? 0), 0).toFixed(4),
    short_line_count: 0,
    revision: 1,
    line_count: lines.length,
    released_at: status === "draft" ? null : new Date().toISOString(),
    closed_at: null,
    cancelled_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by_name: "Demo User",
    lines,
  };
}

let docs: SalesOrderDocument[] = [
  build("so-3", "SO-2026-00003", "draft", customers[2]!, isoDay(0), [line(1, catalog[0]!, 15, 405, isoDay(10))]),
  build("so-2", "SO-2026-00002", "released", customers[1]!, isoDay(-1), [
    line(1, catalog[1]!, 30, 1450, isoDay(5)),
    line(2, catalog[0]!, 10, 1390, isoDay(5)),
  ]),
  build("so-1", "SO-2026-00001", "released", customers[0]!, isoDay(-2), [line(1, catalog[0]!, 40, 389, isoDay(3))]),
];
let seq = 4;

const holds = (s: SalesOrderStatus) => s === "released" || s === "partially_shipped";

/** Re-reserves every released order in number order and refreshes availability. */
function recompute() {
  const left = new Map(catalog.map((c) => [c.id, c.stock]));
  const ordered = [...docs].sort((a, b) => a.number.localeCompare(b.number));
  for (const d of ordered) {
    for (const l of d.lines) {
      const open = Number(l.ordered_base_qty) - Number(l.shipped_base_qty);
      const free = left.get(l.item_id) ?? 0;
      const res = holds(d.status) && l.status === "open" ? Math.max(0, Math.min(open, free)) : 0;
      l.reserved_base_qty = String(res);
      left.set(l.item_id, free - res);
    }
  }
  for (const d of docs) {
    for (const l of d.lines) l.available_base_qty = String(left.get(l.item_id) ?? 0);
    d.short_line_count = holds(d.status)
      ? d.lines.filter((l) => l.status === "open" && Number(l.reserved_base_qty) < Number(l.ordered_base_qty) - Number(l.shipped_base_qty)).length
      : 0;
  }
}
recompute();

const toRow = (d: SalesOrderDocument): SalesOrderRow => ({
  id: d.id,
  number: d.number,
  status: d.status,
  customer_id: d.customer_id,
  customer_code: d.customer_code,
  customer_name: d.customer_name,
  order_date: d.order_date,
  next_requested_date: d.next_requested_date,
  total_amount: d.total_amount,
  currency: d.currency,
  line_count: d.line_count,
  short_line_count: d.short_line_count,
});

const delay = <T,>(v: T, ms = 90) => new Promise<T>((r) => setTimeout(() => r(v), ms));
const isOpen = (s: SalesOrderStatus) => s === "draft" || s === "released" || s === "partially_shipped";
const shortOf = (d: SalesOrderDocument | undefined) =>
  d && holds(d.status)
    ? d.lines.filter((l) => l.status === "open").reduce((s, l) => s + Number(l.ordered_base_qty) - Number(l.reserved_base_qty), 0)
    : 0;

export const demoSoApi = {
  list(p: ListParams, cursor: string | null): Promise<Page<SalesOrderRow>> {
    const q = p.q.trim().toLowerCase();
    const sorted = [...docs].sort((a, b) => (a.order_date === b.order_date ? b.number.localeCompare(a.number) : b.order_date.localeCompare(a.order_date)));
    const filtered = sorted.filter(
      (d) =>
        (p.status === "all" || (p.status === "open" ? isOpen(d.status) : d.status === p.status)) &&
        (!q || d.number.toLowerCase().startsWith(q) || d.customer_name.toLowerCase().includes(q) || d.customer_code.toLowerCase().includes(q)),
    );
    const start = cursor ? filtered.findIndex((d) => `${d.order_date}|${d.number}` === cursor) + 1 : 0;
    const page = filtered.slice(start, start + 50);
    const more = start + 50 < filtered.length;
    const last = page[page.length - 1];
    return delay({ rows: page.map(toRow), nextCursor: more && last ? `${last.order_date}|${last.number}` : null });
  },
  get: (id: string) => delay(structuredClone(docs.find((d) => d.id === id) ?? null), 60),
  save(id: string | null, _company: string, customerId: string, header: SalesOrderHeaderInput, input: SalesOrderLineInput[]): Promise<SaveResult> {
    const cust = customers.find((c) => c.id === customerId) ?? customers[0]!;
    const existing = id ? docs.find((d) => d.id === id) : undefined;
    const orderDate = header.order_date ?? existing?.order_date ?? isoDay(0);
    const lines = input.map((l, i) => {
      const item = catalog.find((c) => c.id === l.item_id) ?? { id: l.item_id, code: l.item_id, description: "", price: 0, stock: 0 };
      const prev = existing?.lines.find((x) => x.id === l.line_id);
      const price = l.unit_price === undefined ? (prev ? Number(prev.unit_price) : item.price) : l.unit_price === null ? null : Number(l.unit_price);
      const ln = line(prev?.line_no ?? i + 1, item, Number(l.qty), price, l.requested_date ?? prev?.requested_date ?? orderDate);
      return { ...ln, id: prev?.id ?? ln.id, shipped_base_qty: prev?.shipped_base_qty ?? "0", status: prev?.status ?? ln.status };
    });
    let doc: SalesOrderDocument;
    if (existing) {
      doc = { ...build(existing.id, existing.number, existing.status, cust, orderDate, lines), revision: existing.revision + 1 };
      docs = docs.map((d) => (d.id === existing.id ? doc : d));
    } else {
      doc = build(`so-${seq}`, `SO-2026-${String(seq).padStart(5, "0")}`, "draft", cust, orderDate, lines);
      seq++;
      docs = [doc, ...docs];
    }
    recompute();
    return delay({ id: doc.id, number: doc.number, status: doc.status, revision: doc.revision, short_qty: shortOf(doc) });
  },
  transition(id: string, action: Transition): Promise<{ short_qty?: number }> {
    const next: Partial<Record<Transition, SalesOrderStatus>> = { release: "released", cancel: "cancelled", close: "closed" };
    docs = docs.map((d) => {
      if (d.id !== id || !next[action]) return d;
      const status = next[action]!;
      return {
        ...d,
        status,
        revision: d.revision + 1,
        lines: status === "released" ? d.lines : d.lines.map((l) => (l.status === "open" ? { ...l, status: "closed" as const } : l)),
      };
    });
    recompute();
    return delay({ short_qty: shortOf(docs.find((d) => d.id === id)) });
  },
  closeLine(id: string, lineId: string): Promise<void> {
    docs = docs.map((d) =>
      d.id === id ? { ...d, revision: d.revision + 1, lines: d.lines.map((l) => (l.id === lineId ? { ...l, status: "closed" as const } : l)) } : d,
    );
    recompute();
    return delay(undefined);
  },
};
